import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AccountingService } from '../../../services/accounting.service';
import { AuthService } from '../../../services/auth.service';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { ChipComponent, ChipColor } from '../../components/inputs/chip/chip.component';

@Component({
  selector: 'app-giving',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BtnBackComponent,
    BtnActionComponent,
    ChipComponent,
    RouterLink,
  ],
  templateUrl: './giving.component.html',
})
export class GivingComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private store: Store<AppState> = inject(Store<AppState>);
  private auth: AuthService = inject(AuthService);
  private accountingService: AccountingService = inject(AccountingService);
  private toastr: ToastrService = inject(ToastrService);
  private logger: NGXLogger = inject(NGXLogger);

  groupId: string | null = null;
  userId: string | null = null;
  groupName: string = 'Group';
  group: any = null;
  fiscalYearStart: Date | null = null;
  fiscalYearEnd: Date | null = null;

  pageForm: FormGroup;
  isSubmitting = false;

  totalFiscalYearContribution = 0;
  currencySymbol = '$';

  activeFundings: any[] = [];
  selectedFunding: any | null = null;
  showPaymentModal = false;

  allPayments: any[] = [];
  displayedPayments: any[] = [];
  pageSize = 10;
  currentPage = 1;
  hasMore = false;

  user: any = null;
  stripe!: Stripe | null;
  elements!: StripeElements | null;
  paymentElement: any = null;
  stripeLoading = false;
  paymentStep: 'amount' | 'card' = 'amount';
  chipColor = ChipColor;

  private exit$ = new Subject<void>();

  constructor() {
    this.groupId = this.route.snapshot.paramMap.get('id');
    this.userId = this.auth.getUid() || null;

    this.pageForm = new FormGroup({
      amount: new FormControl('', [
        Validators.required,
        Validators.min(1),
        Validators.max(100000),
      ]),
      mode: new FormControl('payment', [Validators.required]),
      currency: new FormControl('usd', [Validators.required]),
    });

    this.store
      .select((state) => state.user?.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  getCurrencySymbol(): string {
    const currency = (this.group?.currency || 'usd').toLowerCase();
    switch (currency) {
      case 'eur':
        return '€';
      case 'gbp':
        return '£';
      case 'jpy':
        return '¥';
      case 'inr':
        return '₹';
      case 'usd':
      case 'cad':
      case 'aud':
      case 'nzd':
      default:
        return '$';
    }
  }

  ngOnInit(): void {
    if (!this.groupId || !this.userId) {
      this.toastr.error('Group ID or User not found');
      return;
    }

    // Check for Stripe redirect parameters
    this.route.queryParams.pipe(takeUntil(this.exit$)).subscribe((params) => {
      const status = params['redirect_status'];
      if (status === 'succeeded') {
        this.toastr.success(
          'Thank you! Your donation was processed successfully.',
        );
        // Clean query params
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }
    });

    // Get group info, payments and active funding requests
    combineLatest([
      this.store.select(groupSelector.getGroupById(this.groupId)),
      this.accountingService.getPayments(this.groupId, this.userId),
      this.accountingService.getFunding(this.groupId),
    ])
      .pipe(takeUntil(this.exit$))
      .subscribe(([group, payments, funding]) => {
        if (group) {
          this.group = group;
          this.groupName = group.name;
          this.pageForm.patchValue({ currency: group.currency || 'usd' });
          if (group.fiscalYearStart) {
            this.fiscalYearStart = group.fiscalYearStart.seconds
              ? new Date(group.fiscalYearStart.seconds * 1000)
              : new Date(group.fiscalYearStart);
            this.fiscalYearEnd = group.fiscalYearEnd
              ? group.fiscalYearEnd.seconds
                ? new Date(group.fiscalYearEnd.seconds * 1000)
                : new Date(group.fiscalYearEnd)
              : null;
          } else {
            const currentYear = new Date().getFullYear();
            this.fiscalYearStart = new Date(currentYear, 0, 1, 0, 0, 0, 0);
            this.fiscalYearEnd = new Date(currentYear, 11, 21, 23, 59, 59, 999);
          }
        } else {
          const currentYear = new Date().getFullYear();
          this.fiscalYearStart = new Date(currentYear, 0, 1, 0, 0, 0, 0);
          this.fiscalYearEnd = new Date(currentYear, 11, 21, 23, 59, 59, 999);
        }

        if (payments) {
          const startMs = this.fiscalYearStart.getTime();
          const endMs = this.fiscalYearEnd ? this.fiscalYearEnd.getTime() : Infinity;

          this.allPayments = payments.filter((payment) => {
            const paymentDate = payment.timestamp?.seconds
              ? new Date(payment.timestamp.seconds * 1000)
              : new Date(payment.timestamp);
            const paymentMs = paymentDate.getTime();
            return paymentMs >= startMs && paymentMs <= endMs;
          });
          this.calculateFiscalContributions();
          this.updateDisplayedPayments();
        }

        if (funding) {
          this.activeFundings = funding.filter((f) => f.status === 'active');
        }
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  calculateFiscalContributions() {
    this.totalFiscalYearContribution = 0;
    if (!this.fiscalYearStart) return;

    const startMs = this.fiscalYearStart.getTime();
    const endMs = this.fiscalYearEnd ? this.fiscalYearEnd.getTime() : Infinity;

    this.allPayments.forEach((payment) => {
      if (payment.status === 'succeeded' || payment.status === 'active') {
        const paymentDate = payment.timestamp?.seconds
          ? new Date(payment.timestamp.seconds * 1000)
          : new Date(payment.timestamp);
        const paymentMs = paymentDate.getTime();

        if (paymentMs >= startMs && paymentMs <= endMs) {
          this.totalFiscalYearContribution += payment.amount;
        }
      }
    });
  }

  updateDisplayedPayments() {
    const totalToDisplay = this.currentPage * this.pageSize;
    this.displayedPayments = this.allPayments.slice(0, totalToDisplay);
    this.hasMore = this.allPayments.length > this.displayedPayments.length;
  }

  loadMore() {
    this.currentPage++;
    this.updateDisplayedPayments();
  }

  getPaymentDate(payment: any): Date {
    if (!payment?.timestamp) return new Date();
    return payment.timestamp.seconds
      ? new Date(payment.timestamp.seconds * 1000)
      : new Date(payment.timestamp);
  }

  getPaymentDescription(payment: any): string {
    const isSubscription = !!payment.stripeSubscriptionId;
    const frequency = isSubscription ? 'Recurring monthly' : 'One time';
    
    let typeLabel = 'donation';
    if (payment.type === 'tithe') {
      typeLabel = 'tithe';
    } else if (payment.type === 'fund_donation') {
      typeLabel = 'donation';
    }
    
    return `${frequency} ${typeLabel} to ${this.groupName}`;
  }

  openTitheModal() {
    this.selectedFunding = null;
    this.pageForm.patchValue({ amount: '' });
    this.resetPaymentStep();
    this.showPaymentModal = true;
  }

  selectFundingForDonation(funding: any) {
    this.selectedFunding = funding;
    this.pageForm.patchValue({ amount: '' });
    this.resetPaymentStep();
    this.showPaymentModal = true;
  }

  cancelDonation() {
    this.selectedFunding = null;
    this.showPaymentModal = false;
    this.resetPaymentStep();
  }

  resetPaymentStep() {
    if (this.paymentElement) {
      this.paymentElement.destroy();
      this.paymentElement = null;
    }
    this.elements = null;
    this.paymentStep = 'amount';
    this.stripeLoading = false;
  }

  async proceedToPayment() {
    if (this.pageForm.invalid || !this.groupId) {
      this.logger.warn('proceedToPayment :: invalid form or missing groupId', {
        invalid: this.pageForm.invalid,
        groupId: this.groupId,
      });
      return;
    }

    const amount = this.pageForm.value.amount;
    const currency = this.pageForm.value.currency;
    const mode = this.pageForm.value.mode;

    this.logger.info(
      'proceedToPayment :: starting flow',
      JSON.stringify({
        amount,
        currency,
        mode,
        groupId: this.groupId,
        selectedFunding: this.selectedFunding,
      }),
    );
    this.stripeLoading = true;
    this.paymentStep = 'card';

    try {
      let clientSecret = '';

      if (mode === 'payment') {
        this.logger.debug(
          'proceedToPayment :: calling createDonationPaymentIntent',
        );
        const res = await this.accountingService.createDonationPaymentIntent({
          amount,
          currency,
          groupId: this.groupId,
          fundingId: this.selectedFunding ? this.selectedFunding.id : undefined,
          name: this.user?.name || 'Anonymous',
          email: this.user?.email || '',
        });
        this.logger.info(
          'proceedToPayment :: createDonationPaymentIntent response received',
          { paymentIntentId: res.paymentIntentId },
        );
        clientSecret = res.clientSecret;
      } else {
        this.logger.debug(
          'proceedToPayment :: calling payment_createSetupIntent',
        );
        const functions = getFunctions();
        const createSetupIntentFn = httpsCallable(
          functions,
          'payment_createSetupIntent',
        );
        const res: any = await createSetupIntentFn({
          groupId: this.groupId,
          seats: 0,
          name: this.user?.name || 'Anonymous',
          email: this.user?.email || '',
        });
        this.logger.info(
          'proceedToPayment :: payment_createSetupIntent response received',
        );
        clientSecret = res.data.clientSecret;
      }

      this.logger.debug('proceedToPayment :: loading Stripe instance');
      this.stripe = await loadStripe(environment.STRIPE_PUBLISHABLE_KEY);
      if (!this.stripe) {
        this.logger.error('proceedToPayment :: Stripe failed to load');
        this.toastr.error('Stripe failed to load');
        this.resetPaymentStep();
        return;
      }

      this.logger.debug('proceedToPayment :: initializing Elements');
      this.elements = this.stripe.elements({ clientSecret });
      this.paymentElement = this.elements.create('payment');

      setTimeout(() => {
        this.logger.debug('proceedToPayment :: mounting Payment Element');
        this.paymentElement.mount('#payment-element');
        this.stripeLoading = false;
      }, 50);
    } catch (error: any) {
      this.logger.error(
        'proceedToPayment :: Error occurred',
        JSON.stringify(error),
      );
      this.toastr.error(error.message || 'Failed to initialize payment method');
      this.resetPaymentStep();
    }
  }

  async donate() {
    if (
      this.pageForm.invalid ||
      !this.groupId ||
      !this.stripe ||
      !this.elements
    ) {
      this.logger.warn('donate :: pre-conditions not met', {
        invalidForm: this.pageForm.invalid,
        groupId: this.groupId,
        stripeLoaded: !!this.stripe,
        elementsInitialized: !!this.elements,
      });
      return;
    }

    this.logger.info('donate :: starting donation submission');
    this.isSubmitting = true;

    try {
      const amount = this.pageForm.value.amount;
      const currency = this.pageForm.value.currency;
      const mode = this.pageForm.value.mode;

      if (mode === 'payment') {
        this.logger.debug('donate :: confirming one-time payment');
        const { error, paymentIntent } = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        });

        if (error) {
          this.logger.error('donate :: payment confirmation failed', error);
          this.toastr.error(error.message || 'Payment failed');
          this.isSubmitting = false;
          return;
        }

        this.logger.info('donate :: payment confirmation complete', {
          status: paymentIntent?.status,
        });
        if (paymentIntent?.status === 'succeeded') {
          this.toastr.success(
            'Thank you! Your donation was processed successfully.',
          );
          this.closeModalAndRefresh();
        } else {
          this.logger.warn(
            'donate :: payment intent status was not succeeded',
            { status: paymentIntent?.status },
          );
          this.toastr.warning('Payment status: ' + paymentIntent?.status);
          this.isSubmitting = false;
        }
      } else {
        this.logger.debug('donate :: confirming setup intent for subscription');
        const { error, setupIntent } = await this.stripe.confirmSetup({
          elements: this.elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        });

        if (error) {
          this.logger.error(
            'donate :: setup intent confirmation failed',
            error,
          );
          this.toastr.error(error.message || 'Setup failed');
          this.isSubmitting = false;
          return;
        }

        this.logger.info('donate :: setup intent confirmed', {
          status: setupIntent?.status,
        });
        if (setupIntent?.status === 'succeeded') {
          this.logger.debug('donate :: calling createDonationSubscription');
          const res = await this.accountingService.createDonationSubscription({
            amount,
            currency,
            groupId: this.groupId,
            fundingId: this.selectedFunding
              ? this.selectedFunding.id
              : undefined,
            setupIntentId: setupIntent.id,
          });

          this.logger.info('donate :: subscription creation succeeded', {
            subscriptionId: res.subscriptionId,
            status: res.status,
          });
          this.toastr.success(
            'Thank you! Your monthly donation subscription is active.',
          );
          this.closeModalAndRefresh();
        } else {
          this.logger.warn('donate :: setup intent status was not succeeded', {
            status: setupIntent?.status,
          });
          this.toastr.warning('Setup status: ' + setupIntent?.status);
          this.isSubmitting = false;
        }
      }
    } catch (error: any) {
      this.logger.error(
        'donate :: Error occurred during donation process',
        error,
      );
      this.toastr.error(
        error.message || 'An error occurred. Please try again.',
      );
      this.isSubmitting = false;
    }
  }

  closeModalAndRefresh() {
    this.selectedFunding = null;
    this.showPaymentModal = false;
    this.isSubmitting = false;
    this.resetPaymentStep();
  }
}
